import React, { useRef, useState } from 'react';
import Select, {
  GroupBase,
  MultiValue,
  SelectInstance,
  SingleValue,
  ClearIndicatorProps,
} from 'react-select';

import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';

import { fontWeightClass } from '~/assets/styles/classes/InputClasses';
import { OptionProp } from '~/models/Commons ';

interface SelectProps {
  label: string;
  id: string;
  isMulti?: boolean;
  value?: OptionProp | OptionProp[];
  fontWeight?: 'bold' | 'normal' | 'extrabold';
  onChange: (param: OptionProp | OptionProp[] | null) => void;
  options: OptionProp[];
  hideLabel?: boolean;
  errorLabel?: string;
  isDisabled?: boolean;
  readOnly?: boolean;
  hideErrorLabel?: boolean;
  menuPortal?: boolean;
  isRequired?: boolean;
  placeholder?: string;
  background?: string;
  testId?: string;
}

export const SIMPLSelectDropdown: React.FC<SelectProps> = ({
  id,
  label,
  value,
  options,
  onChange,
  isMulti = false,
  fontWeight = 'bold',
  errorLabel = '',
  hideLabel = false,
  hideErrorLabel = false,
  isDisabled = false,
  readOnly = false,
  menuPortal = false,
  isRequired = false,
  placeholder,
  background = '#F5F5F5',
  testId,
}) => {
  const selectRef =
    useRef<SelectInstance<OptionProp, true, GroupBase<OptionProp>> | null>(null);

  const [focus, setFocus] = useState(false);

  /* ---------------- VALUE CHECK ---------------- */

  const hasSingleValue =
    !Array.isArray(value) && value !== null && value?.label !== '';

  const hasMultiValue = Array.isArray(value) && value.length > 0;

  const hasValue = hasSingleValue || hasMultiValue;

  /* ---------------- ERROR HANDLING ---------------- */

  const showRequiredError = isRequired && !hasValue && !focus;

  const finalErrorLabel = showRequiredError
    ? 'This field is required'
    : errorLabel;

  /* ---------------- HANDLERS ---------------- */

  const handleSelectChange = (
    selectedOption: SingleValue<OptionProp> | MultiValue<OptionProp> | null
  ) => {
    if (readOnly) return;

    if (Array.isArray(selectedOption)) {
      onChange(selectedOption.length ? selectedOption : null);
    } else if (selectedOption && 'value' in selectedOption) {
      onChange(selectedOption);
    } else {
      onChange(null);
    }

    selectRef?.current?.blur();
    setFocus(false);
  };

  /* ---------------- STYLES ---------------- */

  const getBoxShadow = (isFocused: boolean, error?: string) => {
    if (!isFocused) return 'none';
    return error ? '0 0 0 1px red' : '0 0 0 1px #9B0D78';
  };

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      height: '44px',
      backgroundColor: background,
      borderColor: finalErrorLabel ? 'red' : '#B0B3BA',
      boxShadow: getBoxShadow(focus, finalErrorLabel),
      cursor: readOnly ? 'default' : 'pointer',
      opacity: readOnly ? 0.8 : 1,
      '&:hover': {
        borderColor: 'none',
      },
    }),

    option: (provided: any) => ({
      ...provided,
      fontSize: '0.875rem',
      color: '#6B6F7B',
      backgroundColor: '#F5F5F5',
      '&:hover': {
        backgroundColor: '#CFE0FC',
      },
    }),

    placeholder: (provided: any) => ({
      ...provided,
      color: finalErrorLabel ? '#CA3532' : '#6B6F7B',
    }),
  };

  const CustomClearIndicator = (
    props: ClearIndicatorProps<OptionProp, boolean>
  ) => (
    <div {...props.innerProps} className="cursor-pointer">
      <CloseOutlinedIcon
        fontSize="small"
        className="text-gray-500"
        sx={{ width: 24, height: 24 }}
      />
    </div>
  );

  return (
    <div>
      {!hideLabel && (
        <label
          htmlFor={id}
          className={`inline-block text-sm cursor-pointer ${fontWeightClass(
            fontWeight
          )} ${finalErrorLabel ? 'text-error' : ''}`}
        >
          {label}
          {isRequired && <span className="text-red-500"> *</span>}
        </label>
      )}

      <div className="w-full flex flex-col">
        <Select
          id={id}
          data-test-id={testId}
          ref={selectRef}
          menuPlacement="auto"
          isDisabled={isDisabled}
          placeholder={placeholder}
          styles={customStyles}
          options={options}
          isMulti={isMulti}
          isClearable={!readOnly}
          value={hasValue || isMulti ? value : null}
          menuIsOpen={readOnly ? false : focus}
          onFocus={() => {
            if (!readOnly) setFocus(true);
          }}
          onBlur={() => setFocus(false)}
          onChange={handleSelectChange}
          menuPortalTarget={menuPortal ? document.body : null}
          components={{
            IndicatorSeparator: () => null,
            ClearIndicator: CustomClearIndicator,
          }}
          data-testid={testId}
        />

        {!hideErrorLabel && finalErrorLabel && (
          <div className="mt-1 flex items-center gap-1.5 text-sm text-red-600">
            <HighlightOffRoundedIcon
              fontSize="small"
              className="mt-[1px] shrink-0"
            />
            <span>{finalErrorLabel}</span>
          </div>
        )}
      </div>
    </div>
  );
};